import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { colors } from "@zapalacorp/tokens";
import ConfirmationModal from "Components/Dialogs/ConfirmationModal";
import { useDespatchActions } from "../../views/DiaryResourcesView/hooks/useDespatchActions";
import { Color } from "Components/Form/Button";
import { DespatchCrew } from "Models/despatch.crew";
import { DespatchTask } from "Models/despatch.task";
import { Task } from "Models/task";
import { useTranslation } from "react-i18next";
import { PiWarning } from "react-icons/pi";
import { getMuiPopupContainer } from "Themes/dispatchTheme";

import { ConfirmationModalBody, ConfirmationModalBodyText, useStyles } from "./styles";

type Props = {
  onSuccessCancelTask: (task: DespatchTask | Task, crew?: DespatchCrew | null) => void;
};
export type DespatchCancelTaskModalHandles = {
  open: (task: DespatchTask | Task, crew?: DespatchCrew) => void;
  close: () => void;
};

const DespatchCancelTaskComponent: ForwardRefRenderFunction<
  DespatchCancelTaskModalHandles,
  Props
> = ({ onSuccessCancelTask }, ref) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [task, setTask] = useState<DespatchTask | Task | null | undefined>(null);
  const [crew, setCrew] = useState<DespatchCrew | null | undefined>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { useCancelTask } = useDespatchActions();
  const { mutate: cancelTask, isLoading } = useCancelTask(onSuccessCancelTask);

  const onOpenModal = useCallback((dataTask: DespatchTask | Task, dataCrew?: DespatchCrew) => {
    setTask(dataTask);
    setCrew(dataCrew);
    setIsOpen(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setTask(null);
    setIsOpen(false);
  }, []);

  const onSubmit = () => {
    if (task) {
      cancelTask(
        {
          taskId: task.id!,
          task,
          crew,
        },
        {
          onSuccess: () => {
            onCloseModal();
          },
        }
      );
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <ConfirmationModal
      container={getMuiPopupContainer}
      open={isOpen}
      title={translate("cancelTask")}
      closeModal={() => onCloseModal()}
      leftIconTitle={<PiWarning color={colors.error600} />}
      loading={isLoading}
      submit={onSubmit}
      textSubmit={translate("yesCancel")}
      textCancel={translate("noKeepIt")}
      customColorCancel={Color.TEXT_INFO}
      customColorSave={Color.ERROR}
      type="error"
      maxWidth="520px"
      modalFooterCustomClass={classes.modalFooter}
      showCancelButton
    >
      <ConfirmationModalBody>
        <ConfirmationModalBodyText>
          {translate("areYouSureyouWantToCancelTheTask")} {task?.type}{" "}
          {translate("forOrder").toLowerCase()} {task?.job?.reference}?
        </ConfirmationModalBodyText>
        <ConfirmationModalBodyText isError>
          {translate("thisActionCannotBeUndone")}!
        </ConfirmationModalBodyText>
      </ConfirmationModalBody>
    </ConfirmationModal>
  );
};

const DespatchCancelTask = forwardRef(DespatchCancelTaskComponent);

export { DespatchCancelTask };
